/**
 * paletteConfig — the number row 8–0 as colour-palette *types*.
 *
 * Where 1–7 pick the base *field* (visualConfig) and letters are effects that
 * layer on top, these recolour everything. Each type derives its colours from a
 * single **base hue** the user can shift with −/+ while holding the type's key
 * (see PaletteController). Selection writes the type index to `palette.type` and
 * the active type's base hue to `palette.hue`; the shader mirrors them into
 * `uPaletteType` / `uBaseHue`, and `palette()` builds the ramp accordingly.
 *
 * Selection is exclusive — pressing a type key replaces the active one, exactly
 * like the base-visual selectors. Spectrum at base hue 0 reproduces the original
 * fixed rainbow, so the scene looks unchanged until another type/hue is chosen.
 * Adding a type means appending one entry here plus one branch in shaders.ts
 * `palette()` — nothing in the input path.
 */

export interface PaletteDef {
  /** Normalized digit key ('8', '9', '0'). */
  readonly key: string;
  /** Exclusive selection index, written to the bus and read as `uPaletteType`. */
  readonly index: number;
  /** Stable id (matches the branch in shaders.ts `palette()`). */
  readonly id: string;
  /** Human-facing label for the on-screen selector cell. */
  readonly label: string;
  /** Remembered base hue (0..1) each type starts on before any −/+ shift. */
  readonly defaultHue: number;
}

/** paramBus key holding the active palette-type index. */
export const PALETTE_TYPE_PARAM = 'palette.type';
/** paramBus key holding the active type's base hue (0..1). */
export const PALETTE_HUE_PARAM = 'palette.hue';

export const paletteConfig: readonly PaletteDef[] = [
  { key: '8', index: 0, id: 'analogous',     label: 'Analogous',     defaultHue: 0.5 },
  { key: '9', index: 1, id: 'complementary', label: 'Complement',    defaultHue: 0.0 },
  { key: '0', index: 2, id: 'spectrum',      label: 'Spectrum',      defaultHue: 0.0 },
] as const;

/** The type the instrument boots on / Escape resets to (Spectrum = original look). */
export const DEFAULT_PALETTE_INDEX = 2;

/** Fast key -> definition lookup for the palette controller. */
export const paletteByKey: ReadonlyMap<string, PaletteDef> = new Map(
  paletteConfig.map((p) => [p.key, p]),
);
